import {
  restoreElements,
  restoreAppState,
  exportToBlob,
} from "@excalidraw/excalidraw";
import { clearAppStateForDatabase } from "@excalidraw/excalidraw/appState";

import type { ExcalidrawInitialDataState } from "@excalidraw/excalidraw/types";

import { authFetch } from "../auth/authFetch";

// Loads a board's scene from the Chartoon API so the dashboard can open a
// real drawing in the editor via `?boardId=<id>`.
const CLOUD_API_URL =
  import.meta.env.VITE_CLOUD_API_URL ||
  import.meta.env.VITE_APP_API_URL ||
  "http://localhost:8787";

// Where "Back to dashboard" in the canvas menu points. /dashboard is a
// same-origin route (see dashboardRewritePlugin in vite.config.mts), so the
// dev fallback is relative — it works regardless of which port `yarn start`
// picks.
export const CLOUD_DASHBOARD_URL =
  import.meta.env.VITE_CLOUD_DASHBOARD_URL || "/dashboard";

export const CLOUD_DASHBOARD_ENABLED = Boolean(
  import.meta.env.VITE_CLOUD_DASHBOARD_URL ||
    import.meta.env.VITE_APP_API_URL ||
    import.meta.env.DEV,
);

/**
 * Canonical URL for a board — Figma/Miro style, one path per board.
 * `/board/new` is the local-only scratch canvas (see getCloudBoardId).
 * Paired with `getCanvasUrl` in dashboard/lib/api.ts; keep the two in step.
 */
export const boardPath = (boardId: string): string =>
  `/board/${encodeURIComponent(boardId)}`;

const BOARD_PATH_RE = /^\/board\/([^/?#]+)/;

/**
 * The cloud board open in this tab, if any. Reads `/board/<id>`; the legacy
 * `?boardId=<id>` form still resolves so old links and bookmarks keep working.
 * "new" is a local-only scratch canvas — deliberately NOT a cloud board, so
 * nothing it contains is ever written to someone's account.
 */
export const getCloudBoardId = (): string | null => {
  const fromPath = BOARD_PATH_RE.exec(window.location.pathname)?.[1];
  const boardId =
    (fromPath && decodeURIComponent(fromPath)) ||
    new URLSearchParams(window.location.search).get("boardId");
  return boardId && boardId !== "new" ? boardId : null;
};

// ── Canvas images ────────────────────────────────────────────────────────
// Cloud boards skip IndexedDB, so image blobs need a cloud home. They live in
// the private Supabase Storage bucket `board-files` at `<boardId>/<fileId>`
// (fileId is Excalidraw's content hash), and the scene row keeps only the
// metadata manifest. RLS on the bucket mirrors board access (migration 0008).
const BOARD_FILES_BUCKET = "board-files";

// `<boardId>/<fileId>` already in the bucket this session — the manifest that
// comes back from a load counts, so a reload doesn't re-upload everything.
const storedFiles = new Set<string>();

const supabaseConfigured = () => Boolean(import.meta.env.VITE_SUPABASE_URL);

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Upload blobs that aren't in the bucket yet and return the metadata-only
 * manifest to store on the scene. A file whose upload fails keeps its inline
 * dataURL so the save never drops an image; the next save retries it.
 */
const uploadFiles = async (
  boardId: string,
  files: Record<string, any>,
): Promise<Record<string, unknown>> => {
  const { supabase } = await import("../auth/supabase");
  const manifest: Record<string, unknown> = {};
  await Promise.all(
    Object.entries(files).map(async ([fileId, file]) => {
      const { dataURL, ...meta } = file ?? {};
      const path = `${boardId}/${fileId}`;
      if (!dataURL || storedFiles.has(path)) {
        manifest[fileId] = meta;
        return;
      }
      try {
        const blob = await (await fetch(dataURL)).blob();
        const { error } = await supabase.storage
          .from(BOARD_FILES_BUCKET)
          .upload(path, blob, {
            upsert: true,
            contentType: file.mimeType ?? blob.type,
          });
        if (error) {
          throw new Error(error.message);
        }
        storedFiles.add(path);
        manifest[fileId] = meta;
      } catch (error) {
        console.error("board image upload failed", fileId, error);
        manifest[fileId] = file; // keep it inline rather than lose it
      }
    }),
  );
  return manifest;
};

/** Pull blobs back out of the bucket and re-inline them for the editor. */
const downloadFiles = async (
  boardId: string,
  manifest: Record<string, any>,
): Promise<Record<string, unknown>> => {
  const { supabase } = await import("../auth/supabase");
  const files: Record<string, unknown> = {};
  await Promise.all(
    Object.entries(manifest).map(async ([fileId, file]) => {
      // legacy boards stored the dataURL inline — keep it (and leave it out of
      // `storedFiles` so the next save migrates it into the bucket)
      if (file?.dataURL) {
        files[fileId] = file;
        return;
      }
      const path = `${boardId}/${fileId}`;
      const { data, error } = await supabase.storage
        .from(BOARD_FILES_BUCKET)
        .download(path);
      if (error || !data) {
        console.error("board image download failed", fileId, error);
        return; // Excalidraw renders a placeholder for the missing file
      }
      storedFiles.add(path);
      files[fileId] = {
        ...file,
        id: fileId,
        dataURL: await blobToDataURL(data),
      };
    }),
  );
  return files;
};

// ── Scene save ───────────────────────────────────────────────────────────
// Cloud boards deliberately skip LocalData (their scene would clobber the
// shared local-only one), so this request IS the persistence — a save that
// fails silently loses the drawing. Hence: check the status, retry what's
// retryable, and tell the user when it isn't.

/** Revision token from the last load/save, echoed on every PUT so the API can
 * reject a write that would clobber another tab or collaborator (409). */
let sceneRev: string | null = null;

// The editor's onChange fires on every AppState update — selecting a shape,
// panning, switching tools — not just on edits. Without a dirty check the 2s
// debounce turns idle clicking into a full-scene PUT (and a whole-jsonb row
// rewrite in Postgres) every 2 seconds. `versionNonce` is regenerated on every
// element mutation, so an order-sensitive fold over it fingerprints the scene
// for the price of one integer multiply per element.
const sceneSignature = (
  elements: readonly unknown[],
  files: Record<string, unknown> | undefined,
  extra: string,
): string => {
  let hash = elements.length;
  for (const element of elements as { versionNonce: number }[]) {
    hash = (Math.imul(hash, 31) + (element.versionNonce | 0)) | 0;
  }
  return `${hash}:${Object.keys(files ?? {}).length}:${extra}`;
};

/**
 * The slice of AppState a cloud board persists. Cloud boards skip LocalData, so
 * without this the board kept only `viewBackgroundColor` and lost its grid
 * settings on every reload.
 *
 * `clearAppStateForDatabase` is Excalidraw's own answer for a scene stored on a
 * server and opened by more than one person — grid config, background, locked
 * multi-selections. It deliberately omits scroll/zoom so collaborators don't
 * yank each other's viewport, and `loadBoardFromCloud` passes
 * `scrollToContent: true` anyway, which would override them. Per-user tool
 * preferences (currentItem*) are account-level and sync through users.prefs.
 */
const persistedAppState = (appState: object): Record<string, unknown> =>
  clearAppStateForDatabase(
    appState as Parameters<typeof clearAppStateForDatabase>[0],
  );

/** Signature of the last scene the server accepted. Only ever set after a
 * successful PUT, so a failed save always re-sends. */
let lastSavedSignature: string | null = null;
/** Signature the last uploaded thumbnail was rendered from. */
let lastThumbnailSignature: string | null = null;

type SaveJob = {
  boardId: string;
  elements: readonly unknown[];
  /** already reduced to the persisted slice — see persistedAppState */
  appState: Record<string, unknown>;
  files?: Record<string, unknown>;
  signature: string;
};

const RETRY_INTERVAL_MS = 15_000;

let pending: SaveJob | null = null;
let saving = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** Set when the *board* is unwritable no matter what we send (stale revision,
 * view-only, deleted), so every subsequent edit doesn't re-toast and re-fail.
 * Cleared by a fresh load — which is exactly the fix the toast asks for. */
let blocked = false;
/** Set whenever the last save didn't land, whatever the reason. Drives the
 * before-unload warning; a 413 sets this but not `blocked`, because deleting
 * an image genuinely makes the next save succeed. */
let unsaved = false;

type SaveStatus = "saved" | "retrying" | "blocked";
let notify: ((status: SaveStatus, message?: string) => void) | null = null;

/** Wire a UI channel (a toast, in App.tsx) for save failures. */
export const onCloudSaveStatus = (
  handler: ((status: SaveStatus, message?: string) => void) | null,
): void => {
  notify = handler;
};

/** True when edits exist that the cloud has refused or hasn't taken yet —
 * drives the before-unload warning so closing the tab can't quietly drop them.
 * Deliberately NOT true for a save merely in flight: the unload flush sets
 * `keepalive`, so those still land, and warning on every close would be noise.
 */
export const hasUnsavedCloudChanges = (): boolean => unsaved;

const putScene = async (job: SaveJob): Promise<void> => {
  const payloadFiles =
    job.files && supabaseConfigured()
      ? await uploadFiles(job.boardId, job.files)
      : job.files;
  // no JSON content-type on purpose: it would force a CORS preflight, and
  // Chrome rejects keepalive requests that need one. The API parses the body
  // as JSON regardless of content type.
  const res = await authFetch(
    `${CLOUD_API_URL}/api/boards/${job.boardId}/scene`,
    {
      method: "PUT",
      // keepalive only on the hidden-tab flush: Chrome rejects keepalive
      // bodies >64KB, so setting it on every save would break saves of any
      // non-trivial board.
      keepalive: document.visibilityState === "hidden",
      body: JSON.stringify({
        rev: sceneRev,
        scene: {
          elements: job.elements,
          appState: job.appState,
          files: payloadFiles,
        },
      }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      message?: string;
      error?: string;
    } | null;
    const error = new Error(
      body?.message ?? body?.error ?? `save failed (${res.status})`,
    ) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  const body = (await res.json().catch(() => null)) as { rev?: string } | null;
  if (body?.rev) {
    sceneRev = body.rev;
  }
};

/** A 4xx other than 429 means retrying sends the identical request to the
 * identical rejection — conflict, gone, too big, no permission. */
const isFatal = (status?: number): boolean =>
  status !== undefined && status >= 400 && status < 500 && status !== 429;

/** …but "too large" is a property of the payload, not the board: once the user
 * deletes an image the next save is a different, smaller request. Don't wedge
 * the queue on it. */
const isRetryableOnEdit = (status?: number): boolean => status === 413;

const fatalMessage = (status: number | undefined, fallback: string): string => {
  switch (status) {
    case 409:
      return "This board changed in another tab or window. Reload to keep editing — changes made here since then are not saved.";
    case 403:
      return "You have view-only access to this board, so your changes aren't being saved.";
    case 404:
      return "This board no longer exists, so your changes aren't being saved.";
    case 413:
      return "This board is too large to save. Remove some images or split it across boards.";
    default:
      return fallback;
  }
};

const drain = async (): Promise<void> => {
  if (saving || !pending || blocked) {
    return;
  }
  const job = pending;
  pending = null;
  saving = true;
  try {
    await putScene(job);
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    unsaved = false;
    lastSavedSignature = job.signature;
    notify?.("saved");
  } catch (error) {
    const status = (error as { status?: number }).status;
    unsaved = true;
    if (isFatal(status)) {
      blocked = !isRetryableOnEdit(status);
      notify?.("blocked", fatalMessage(status, (error as Error).message));
    } else {
      // offline / 5xx / rate limited — keep the work and try again. A newer
      // edit that landed mid-flight wins; this job is the older snapshot.
      pending = pending ?? job;
      if (!retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          void drain();
        }, RETRY_INTERVAL_MS);
      }
      notify?.("retrying", "Couldn't reach the server — retrying…");
    }
  } finally {
    saving = false;
  }
  if (pending && !retryTimer && !blocked) {
    void drain();
  }
};

/**
 * Write the current scene back to the cloud API so the dashboard and other
 * devices see edits. Never rejects — failures are retried (transient) or
 * surfaced via `onCloudSaveStatus` (fatal). Image blobs go to Supabase Storage
 * first (see uploadFiles) so the scene payload stays small; without Supabase
 * configured (DEV_AUTH/KV mode) they ride inline as before.
 */
export const saveBoardToCloud = async (
  boardId: string,
  elements: readonly unknown[],
  // Only ever reduced via persistedAppState (which takes `object`); a bare
  // `object` accepts real AppState and test literals alike, with no
  // excess-property errors.
  appState: object,
  files?: Record<string, unknown>,
): Promise<void> => {
  // Hashed as well as sent: a grid toggle changes no element's versionNonce,
  // so without it in the signature the change would never reach the server.
  const persisted = persistedAppState(appState);
  const signature = sceneSignature(elements, files, JSON.stringify(persisted));
  // Nothing changed since the server last accepted this scene — a PUT would
  // rewrite the row with identical bytes. (Only set on success, so a failed
  // save still retries.)
  if (signature === lastSavedSignature) {
    return;
  }
  pending = { boardId, elements, appState: persisted, files, signature };
  await drain();
};

// Board thumbnails for the dashboard grid. Riding the debounced save,
// throttled hard so PNG export doesn't run on every edit burst. Supabase
// Storage only — KV/dev mode has no bucket, cards keep the emoji fallback.
let lastThumbnailAt = 0;
const THUMBNAIL_INTERVAL_MS = 30_000;

export const maybeUploadThumbnail = (
  boardId: string,
  elements: readonly unknown[],
  appState: { viewBackgroundColor?: string },
  files?: Record<string, unknown>,
): void => {
  if (!import.meta.env.VITE_SUPABASE_URL) {
    return;
  }
  if (!elements.length) {
    return;
  }
  const now = Date.now();
  if (now - lastThumbnailAt < THUMBNAIL_INTERVAL_MS) {
    return;
  }
  // Re-exporting a PNG of an unchanged scene every 30s costs a full render, a
  // Storage upload and a board PATCH for an identical image. Only what the PNG
  // actually depends on — a grid toggle doesn't change the export.
  const signature = sceneSignature(
    elements,
    files,
    appState.viewBackgroundColor ?? "",
  );
  if (signature === lastThumbnailSignature) {
    return;
  }
  lastThumbnailSignature = signature;
  lastThumbnailAt = now;
  void (async () => {
    try {
      const blob = await exportToBlob({
        elements: elements as never[],
        appState: {
          exportBackground: true,
          viewBackgroundColor: appState.viewBackgroundColor,
        },
        files: (files ?? null) as never,
        mimeType: "image/png",
        maxWidthOrHeight: 480,
      });
      const { supabase } = await import("../auth/supabase");
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user.id;
      if (!uid) {
        return;
      }
      const path = `${uid}/${boardId}.png`;
      const { error } = await supabase.storage
        .from("thumbnails")
        .upload(path, blob, { upsert: true, contentType: "image/png" });
      if (error) {
        throw new Error(error.message);
      }
      const { data } = supabase.storage.from("thumbnails").getPublicUrl(path);
      await authFetch(`${CLOUD_API_URL}/api/boards/${boardId}`, {
        method: "PATCH",
        body: JSON.stringify({ thumbnailUrl: `${data.publicUrl}?v=${now}` }),
      });
    } catch (error) {
      console.error("thumbnail upload failed", error);
      lastThumbnailAt = 0; // let the next save retry
      lastThumbnailSignature = null; // …and don't let the dedupe suppress it
    }
  })();
};

// Last board name written to (or read from) the cloud, so renaming via the
// top-left name field syncs to the dashboard without re-PATCHing every save.
let lastSyncedName: string | null = null;

/** Push a rename to the cloud so the dashboard shows the new name. */
export const syncBoardNameToCloud = (
  boardId: string,
  name?: string | null,
): void => {
  const trimmed = (name ?? "").trim();
  // empty names are rejected by the API; keep the previous name instead
  if (!trimmed || trimmed === lastSyncedName) {
    return;
  }
  lastSyncedName = trimmed;
  authFetch(`${CLOUD_API_URL}/api/boards/${boardId}`, {
    method: "PATCH",
    body: JSON.stringify({ name: trimmed }),
  }).catch((error) => {
    console.error("cloud board rename failed", error);
  });
};

export const loadBoardFromCloud = async (
  boardId: string,
): Promise<ExcalidrawInitialDataState> => {
  const res = await authFetch(`${CLOUD_API_URL}/api/boards/${boardId}/scene`);
  if (!res.ok) {
    throw new Error(`Failed to load board ${boardId} (${res.status})`);
  }
  const { board, scene } = (await res.json()) as {
    board?: { name?: string };
    scene: {
      elements: unknown[];
      appState: Record<string, unknown>;
      files?: Record<string, unknown>;
      rev?: string;
    };
  };

  lastSyncedName = board?.name ?? null;
  // this load is now the base revision every save writes against; a reload is
  // also how the user recovers from a 409, so clear the blocked state
  sceneRev = scene.rev ?? null;
  blocked = false;
  unsaved = false;
  pending = null;
  // A different board (or a reload after a conflict) must not inherit the
  // previous scene's signature, or its first save would be suppressed.
  lastSavedSignature = null;
  lastThumbnailSignature = null;

  // image blobs live in Supabase Storage; the scene carries their manifest
  // (cloud boards don't use IndexedDB, so this is their only file store)
  const files =
    scene.files && supabaseConfigured()
      ? await downloadFiles(boardId, scene.files)
      : scene.files;

  return {
    elements: restoreElements(scene.elements as any, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    files: (files as ExcalidrawInitialDataState["files"]) ?? undefined,
    appState: restoreAppState(
      // older API deploys may omit `board` — the name is display-only
      {
        ...scene.appState,
        ...(board?.name ? { name: board.name } : {}),
      } as any,
      null,
    ),
    scrollToContent: true,
  };
};
